import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { isCancel } from 'axios'
import { debounce } from 'lodash'
import {
  logger,
  toLocalISOString,
  toLocalISOStringEndOfDay,
  toLocalISOStringStartOfDay,
} from 'shared/lib'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { RequestCancelAxios } from 'shared/api/lib'
import {
  ChatbotApi,
  ChatbotConversationStatus,
  type IChatbotConversationStatus,
  type IParamsGetChatbotAnalyticList,
  type IResponseChatbotAnalyticList,
  ChatbotAnalyticListFilter,
  ChatbotAnalyticListItem,
} from 'entities/Chatbot'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'

export class ChatbotAnalyticListStore {
  page = 1
  total = 0
  limit = 10
  search = ''
  status: IChatbotConversationStatus | null = null
  activeStatus: IChatbotConversationStatus | null = null

  loading = true
  loadingScheduled = false
  initialLoading = true

  private _contactsDetailsManageStore: ContactsDetailsManageStore
  private _calendarFilterStore = new CalendarFilterStore()
  private _previousSearchValue = ''

  private _cancelRequest = new RequestCancelAxios()
  private _listMap = new Map<number, ChatbotAnalyticListItem>()

  constructor(public id: number, private _entities: IEntitiesStore) {
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    makeAutoObservable(this)

    reaction(() => this._requestParams, this._loadDataWithDebounce)
  }

  get items() {
    return Array.from(this._listMap.values())
  }

  get isEmpty() {
    if (this.initialLoading || this.loadingScheduled || this.loading) return false

    return !this._listMap.size
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  reset = () => {
    this._contactsDetailsManageStore.reset()
  }

  init = () => {
    this._contactsDetailsManageStore.init()
  }

  get calendarFilterStore() {
    return this._calendarFilterStore
  }

  get isQuietHoursView() {
    return this.activeStatus === ChatbotConversationStatus.QuietHours
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: async (data) => {
        try {
          this._contactsDetailsManageStore.setDisabled(true)

          const inbox = data.inboxId
            ? this._entities.inboxesStore.getItem(data.inboxId)
            : data.numberId
            ? this._entities.inboxesStore.getInboxByNumber(data.numberId)
            : undefined

          await this._contactsDetailsManageStore.onOpenContactDetails(
            {
              contactId: data.contactId,
              inboxId: inbox?.id,
            },
            'textbot',
            true
          )
        } catch (e) {
          console.log(e)
        }
      },
    }
  }

  setSearch = (value: string) => {
    this.search = value
    this.page = 1
    this.loadingScheduled = true
  }

  private _debouncedTrackSearchActivity = debounce((searchQuery: string) => {
    this._entities.organizationStore.handleTrackOrganizationActivity({
      event: 'phi.contact.searched',
      data: {
        search_query: searchQuery,
      },
    })
  }, 2000)

  private _loadDataWithDebounce = () => {
    this._cancelRequest.init()

    runInAction(() => {
      this.loading = true
    })

    this._debounceLoadData()
  }

  private _debounceLoadData = debounce(() => {
    this.loadData()
  }, 500)

  loadData = async () => {
    let isCancelled = false

    try {
      runInAction(() => {
        this.loading = true
      })

      this._cancelRequest.init()

      const { data } = await ChatbotApi.getChatbotAnalyticList(this._requestParams, {
        cancelToken: this._cancelRequest.token,
      })

      if (
        this.search !== this._previousSearchValue &&
        this._entities.organizationStore.isActivityLoggingEnabled
      ) {
        this._previousSearchValue = this.search
        this._debouncedTrackSearchActivity(this.search)
      }

      runInAction(() => {
        this.activeStatus = this.status
      })

      this._setData(data)

      runInAction(() => {
        this.loading = false
      })
    } catch (e) {
      if (isCancel(e)) {
        isCancelled = true
      }
      logger.error(e)
    } finally {
      if (!isCancelled) {
        runInAction(() => {
          this.loading = false
          this.initialLoading = false
          this.loadingScheduled = false
        })
      }
    }
  }

  changeStatus = (status: IChatbotConversationStatus | null) => {
    this.page = 1
    this.status = status
    this.loadingScheduled = true
  }

  changePaging = (page: number, limit: number) => {
    this.page = page
    this.limit = limit
    this.loadingScheduled = true
  }

  setLoading = (loading: boolean) => {
    this.loading = loading
  }

  dispose = () => {
    this._cancelRequest.reset()
  }

  private get _requestParams(): IParamsGetChatbotAnalyticList {
    return {
      id: this.id,
      page: this.page,
      limit: this.limit,
      search: this.search,
      [ChatbotAnalyticListFilter.Status]: this.status,
      [ChatbotAnalyticListFilter.From]: toLocalISOString(
        this.calendarFilterStore.sendDate?.from
          ? toLocalISOStringStartOfDay(this.calendarFilterStore.sendDate.from)
          : null
      ),
      [ChatbotAnalyticListFilter.To]: toLocalISOString(
        this.calendarFilterStore.sendDate?.to
          ? toLocalISOStringEndOfDay(this.calendarFilterStore.sendDate.to)
          : null
      ),
    }
  }

  private _setData = ({ data, meta }: IResponseChatbotAnalyticList) => {
    this._listMap.clear()
    data.forEach((item) => {
      const listItem = new ChatbotAnalyticListItem(item)
      this._listMap.set(listItem.id, listItem)
    })

    this.page = meta.current_page
    this.total = meta.total
  }
}
